import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { sweetAlertMixin } from "../../helpers/sweetAlert";
import { authApiCalls } from "../../services/authApiCalls";
import {
  AuthState,
  ForgotPasswordData,
  LoginCredentials,
  RegisterData,
  ResetPasswordData,
} from "../../types/auth";

const initialState: AuthState = {
  user: {
    access_token: "",
    expires_in: 0,
    token_type: "",
    user: {
      email: JSON.parse(sessionStorage.getItem("user")!)?.email || "",
      id: JSON.parse(sessionStorage.getItem("user")!)?.id || 0,
      name: JSON.parse(sessionStorage.getItem("user")!)?.name || "",
    },
  },
  loading: false,
  error: "",
  success: false,
  token: sessionStorage.getItem("token") || null,
};

export const registerUser = createAsyncThunk(
  "auth/register-user",
  async (values: RegisterData, { rejectWithValue }) => {
    try {
      const response = await authApiCalls.register(values);

      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }
      return response;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 422) {
        return rejectWithValue("El email ya se encuentra registrado");
      } else {
        return rejectWithValue(
          "Ocurrió un error inesperado, intente nuevamente mas tarde"
        );
      }
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login-user",
  async (values: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authApiCalls.login(values);

      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }
      return response;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 401) {
        sweetAlertMixin("error", "Email o contraseña invalidos.");
        return rejectWithValue("Email o contraseña invalidos.");
      } else if (axiosError.response?.status === 403) {
        sweetAlertMixin(
          "error",
          "Cuenta sin habilitar, verifica tu email para activarla."
        );
        return rejectWithValue(
          "Cuenta sin habilitar, verifica tu email para activarla."
        );
      } else {
        sweetAlertMixin(
          "error",
          "Ocurrió un error inesperado, intente nuevamente mas tarde."
        );
        return rejectWithValue(
          "Ocurrió un error inesperado, intente nuevamente mas tarde"
        );
      }
    }
  }
);

export const forgotPasswordAsync = createAsyncThunk(
  "auth/forgot-password",
  async (values: ForgotPasswordData, { rejectWithValue }) => {
    try {
      const response = await authApiCalls.forgotPassword(values);

      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }

      sweetAlertMixin(
        "success",
        "Te enviamos un email para que puedas cambiar tu clave."
      );

      return response.data; // Don't need fulfillWithValue, just return the data
    } catch (error: unknown) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "status" in error.response &&
        error.response.status === 401
      ) {
        sweetAlertMixin("error", "El email no esta registrado");
        return rejectWithValue("El email no esta registrado");
      } else {
        sweetAlertMixin(
          "error",
          "Ocurrió un error inesperado, intente nuevamente mas tarde."
        );
        return rejectWithValue(
          "Ocurrió un error inesperado, intente nuevamente mas tarde."
        );
      }
    }
  }
);

export const resetPasswordAsync = createAsyncThunk(
  "auth/reset-password",
  async (values: ResetPasswordData, { rejectWithValue }) => {
    try {
      const response = await authApiCalls.resetPassword(values);
      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }
      sweetAlertMixin("success", "Tu clave fue modificada con éxito.");
      return response.data;
    } catch {
      sweetAlertMixin(
        "error",
        "Ocurrió un error inesperado, intente nuevamente mas tarde."
      );
      return rejectWithValue(
        "Ocurrió un error inesperado, intente nuevamente mas tarde"
      );
    }
  }
);

export const registerSuccess = createAsyncThunk(
  "auth/register-success",
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authApiCalls.registerSuccess(token);
      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch {
      return rejectWithValue(
        "Ocurrió un error inesperado, intente nuevamente mas tarde"
      );
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetState: state => {
      state.error = "";
      state.success = false;
    },
    logout: state => {
      sessionStorage.clear();
      state.user = {
        access_token: "",
        expires_in: 0,
        token_type: "",
        user: {
          email: "",
          id: 0,
          name: "",
        },
      };
      state.loading = false;
      state.error = "";
      state.success = false;
      state.token = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(registerUser.pending, state => {
        state.loading = true;
        state.success = false;
        state.error = "";
      })
      .addCase(registerUser.fulfilled, state => {
        state.loading = false;
        state.success = true;
        state.error = "";
      })
      .addCase(registerUser.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload as string;
      })
      .addCase(loginUser.pending, state => {
        state.loading = true;
        state.success = false;
        state.error = "";
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        const response = payload as {
          data: {
            user: { id: number; name: string; email: string };
            access_token: string;
          };
        };
        const user = {
          id: response.data.user.id,
          name: response.data.user.name,
          email: response.data.user.email,
        };
        sessionStorage.setItem("user", JSON.stringify(user));
        sessionStorage.setItem("token", response.data.access_token);
        state.token = response.data.access_token;
        state.loading = false;
        state.user = response.data;
      })
      .addCase(loginUser.rejected, state => {
        state.loading = false;
        state.user = {
          access_token: "",
          expires_in: 0,
          token_type: "",
          user: {
            email: "",
            id: 0,
            name: "",
          },
        };
      })
      .addCase(forgotPasswordAsync.pending, state => {
        state.loading = true;
        state.success = false;
        state.error = "";
      })
      .addCase(forgotPasswordAsync.fulfilled, state => {
        state.loading = false;
        // state.success = true;
        // state.error = '';
      })
      .addCase(forgotPasswordAsync.rejected, state => {
        state.loading = false;
        state.success = false;
      })
      .addCase(resetPasswordAsync.pending, state => {
        state.loading = true;
        state.success = false;
        state.error = "";
      })
      .addCase(resetPasswordAsync.fulfilled, state => {
        state.loading = false;
        state.success = true;
        // state.error = '';
      })
      .addCase(resetPasswordAsync.rejected, state => {
        state.loading = false;
        state.success = false;
      })
      .addCase(registerSuccess.pending, state => {
        state.loading = true;
        state.success = false;
        state.error = "";
      })
      .addCase(registerSuccess.fulfilled, state => {
        state.loading = false;
        state.success = true;
        state.error = "";
      })
      .addCase(registerSuccess.rejected, (state, { payload }) => {
        state.error = payload as string;
        state.loading = false;
        state.success = false;
      });
  },
});

export const { resetState, logout } = authSlice.actions;

export default authSlice.reducer;
